import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product Barcode.
 * This component listens for variant update events and updates the Barcode display accordingly.
 * It handles Barcode updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} barcodeContainer - The container element for the Barcode
 * @property {HTMLElement} barcode - The span element that displays the Barcode text
 *
 * @extends {Component<Refs>}
 */

class ProductBarcodeComponent extends Component {
  requiredRefs = ['barcodeContainer', 'barcode'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateBarcode);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateBarcode);
  }

  /**
   * Updates the Barcode.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateBarcode = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Use the variant data from the event
    // The variant is in event.detail.resource
    if (event.detail.resource) {
      const variantBarcode = event.detail.resource.barcode || '';

      if (variantBarcode) {
        // Show the component and update the Barcode
        this.style.display = 'block';
        this.refs.barcode.textContent = `EAN: ${variantBarcode}`;
      } else {
        // Hide the entire component when Barcode is empty
        this.style.display = 'none';
        this.refs.barcode.textContent = '';
      }
    }
  };
}


if (!customElements.get('product-barcode-component')) {
  customElements.define('product-barcode-component', ProductBarcodeComponent);
}
